import React, { useMemo, useRef } from 'react';
import produce from 'immer';
import { orderBy, uniqBy } from 'lodash-es';
import { MAX_WAVEFORM_COMMENTS } from '@soundxyz/utils/const';
import { uuidv4 } from '@soundxyz/utils/random';
import type {
  FragmentType,
  WaveformCommentInfoFragment,
  WaveformCommentsReleaseInfoFragment,
} from '@graphql/reactQuery';
import {
  getFragment,
  makeFragmentData,
  setQueryData,
  WaveformCommentInfoFragmentDoc,
  WaveformCommentsReleaseInfoFragmentDoc,
  WaveformExpandedPlayerDocument,
  WaveformExpandedPlayerInfoFragmentDoc,
} from '@graphql/reactQuery';
import { useForceUpdate } from '@hooks/useForceUpdate';
import { useSubscribeReleaseNftsComments } from '@hooks/useSubscribeRelease';
import { useAudioController } from '@store/audio/AudioController';
import { useAudioPosition } from '@store/audio/AudioPosition';
import { getSongSlotByAudioPosition } from '@utils/helpers';
import { UserComment } from './UserComment';

export type PlayCommentArgs = {
  commentId: string;
  commentTimestamp?: number;
};

const MAX_COMMENT_OPEN_TIME_MS = 5000;

// TODO: https://linear.app/sound/issue/ENG-4932/consolidate-logic-for-waveform-comment-hero-and-expanded-player
export const WaveformComments = React.memo(function WaveformComments({
  width,
  release: releaseFragment,
}: {
  release: FragmentType<WaveformCommentsReleaseInfoFragmentDoc>;
  width: number;
}) {
  const release = getFragment(WaveformCommentsReleaseInfoFragmentDoc, releaseFragment);
  useSubscribeReleaseNftsComments({
    releaseId: release.id,
    onData({ data: { releaseNftCommentUpdate } }) {
      const releaseArtistSoundHandle = release.artist.soundHandle;
      // This doesn't really happen
      if (!releaseArtistSoundHandle) return;

      setQueryData(
        {
          query: WaveformExpandedPlayerDocument,
          variables: {
            releaseId: release.id,
          },
        },
        previousData => {
          if (!previousData) return previousData;

          return produce(previousData, draft => {
            if (!draft.data.release) return;

            const releaseDraftFragment = getFragment(
              WaveformCommentsReleaseInfoFragmentDoc,
              getFragment(WaveformExpandedPlayerInfoFragmentDoc, draft.data.release),
            );

            if (releaseDraftFragment.topNftsWithComment.length >= MAX_WAVEFORM_COMMENTS) return;

            const data = releaseNftCommentUpdate.map(nft => {
              return {
                id: nft.id,
                serialNumber: nft.serialNumber,
                ...makeFragmentData(
                  {
                    id: nft.id,
                    avatarUrl: nft.ownerAvatarUrl,
                    isGoldenEgg: nft.isGoldenEgg,
                    comment: {
                      id: uuidv4(),
                      message: nft.commentMessage,
                      updatedAt: new Date().toISOString(),
                    },
                    ownerArtistName: nft.ownerArtistName,
                    ownerPublicAddress: nft.ownerPublicAddress,
                    ownerReleaseNftCount: nft.ownerReleaseNftCount,
                    ownerUsername: nft.ownerUsername,
                    ownerWebappUri: nft.ownerWebappUri,
                    serialNumber: nft.serialNumber,
                    songSlot: nft.songSlot,
                    updatedAt: new Date(nft.updatedAt).getTime(),
                  },
                  WaveformCommentInfoFragmentDoc,
                ),
              } satisfies WaveformCommentsReleaseInfoFragment['topNftsWithComment'][number];
            });

            releaseDraftFragment.topNftsWithComment = orderBy(
              uniqBy([...data, ...releaseDraftFragment.topNftsWithComment], v => v.id),
              v => v.serialNumber,
              'asc',
            ).slice(0, MAX_WAVEFORM_COMMENTS);
          });
        },
      );
    },
  });

  const commentsToDisplay = useMemo(
    () =>
      release.topNftsWithComment.map(comment =>
        getFragment(WaveformCommentInfoFragmentDoc, comment),
      ),
    [release.topNftsWithComment],
  );

  const { position } = useAudioPosition();
  const { hideExpandedPlayer, activeTrackId } = useAudioController();
  const isActiveTrack = activeTrackId === release.track.id;

  const currentSongSlot = getSongSlotByAudioPosition({
    position,
    trackDuration: release.track.duration,
  });

  const commentToShowRef = React.useRef<WaveformCommentInfoFragment | null>(null);
  const commentToShow = commentsToDisplay?.find(comment => comment?.songSlot === currentSongSlot);

  if (commentToShow) {
    commentToShowRef.current = commentToShow;
  }

  const lastCommentShownTime = React.useRef<number | null>(null);

  const currentCommentToShow = commentToShowRef.current;

  const forceUpdate = useForceUpdate();

  // keeps track of the timeout that will force an update to the component
  const isUpdating = useRef<ReturnType<typeof setTimeout> | undefined>();

  const shouldShowComment = (() => {
    if (hideExpandedPlayer) return false;
    if (!isActiveTrack) return false;

    if (commentToShow != null || lastCommentShownTime.current == null) {
      lastCommentShownTime.current = Date.now();

      return true;
    }

    const timeSinceLastCommentShown = Date.now() - lastCommentShownTime.current;

    // if the comment is still open, we want to keep it open for a bit longer
    if (timeSinceLastCommentShown < MAX_COMMENT_OPEN_TIME_MS) {
      //clear to prevent updating the component multiple times
      clearTimeout(isUpdating.current);

      // set a new timeout to update the component after the buffer time
      isUpdating.current = setTimeout(forceUpdate, MAX_COMMENT_OPEN_TIME_MS);
      return true;
    }

    return false;
  })();

  return (
    <>
      {commentsToDisplay.map(nft => (
        <UserComment
          key={nft.id}
          nft={nft}
          isOpen={shouldShowComment && nft.id === currentCommentToShow?.id}
          release={release}
          width={width}
        />
      ))}
    </>
  );
});
